import React from 'react';
import {
  Chart,
  ChartSeries,
  ChartSeriesItem,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartValueAxis,
  ChartValueAxisItem,
  ChartLegend,
  ChartTitle,
} from '@progress/kendo-react-charts';

import { ITimeUsageReport } from '../../models/TimeUsageReport.model';
import { locale } from '../../common/localization/localizationService';
import { diffMinutes } from '../../utils/GeneralUtils';

export interface ITimeUsageReportChartsProps {
  data: Array<ITimeUsageReport>;
}

export default function TimeUsageReportCharts(
  props: ITimeUsageReportChartsProps,
): JSX.Element {
  const modifiedData = props.data.map((order) => {
    const downloadTime = new Date(order.downloadTime);
    const firstSigned = new Date(order.firstSigned);
    const lastSigned = new Date(order.lastSigned);
    const orderDate = new Date(order.orderDate);

    return {
      ...order,
      orderDate,
      routeName: order.routeName,
      effectiveTime: diffMinutes(firstSigned, lastSigned),
      usageTime: diffMinutes(downloadTime, lastSigned),
      routeData: { name: order.routeName },
    };
  });

  const grouped: Record<string, typeof modifiedData> = {};

  modifiedData.forEach((item) => {
    if (!grouped[item.routeName]) {
      grouped[item.routeName] = [];
    }
    grouped[item.routeName].push(item);
  });

  const formatMinutesToTime = (minutes: number): string => {
    console.log(minutes);
    const totalSeconds = Math.round(minutes * 60);

    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;

    return [
      String(h).padStart(2, '0'),
      String(m).padStart(2, '0'),
      String(s).padStart(2, '0'),
    ].join(':');
  };

  return (
    <div className="time-usage-report-chart filter-open">
      {Object.entries(grouped).map(([routeName, items]) => {
        const sorted = [...items].sort(
          (a, b) => a.orderDate.getTime() - b.orderDate.getTime(),
        );

        const categories = sorted.map((x) => x.orderDate);

        return (
          <div key={routeName} style={{ marginBottom: 40 }}>
            <Chart>
              <ChartTitle
                text={`${locale.timeUsageReport._orderName}: ${routeName}`}
              />
              <ChartLegend position="top" />

              <ChartCategoryAxis>
                <ChartCategoryAxisItem
                  categories={categories}
                  baseUnit="days"
                  labels={{ rotation: 'auto' }}
                />
              </ChartCategoryAxis>

              <ChartValueAxis>
                <ChartValueAxisItem
                  title={{ text: locale.timeUsageReport._time }}
                  labels={{
                    content: (e) =>
                      `${Math.round(e.value)} ${locale.timeUsageReport._minutes}`,
                  }}
                />
              </ChartValueAxis>

              <ChartSeries>
                <ChartSeriesItem
                  type="line"
                  name={locale.timeUsageReport._effectiveTime}
                  data={sorted.map((x) => x.effectiveTime)}
                  tooltip={{
                    visible: true,
                    render: (props) => (
                      <span>
                        {locale.timeUsageReport._effectiveTime}:{' '}
                        {formatMinutesToTime(props.point.value)}
                      </span>
                    ),
                  }}
                />

                <ChartSeriesItem
                  type="line"
                  name={locale.timeUsageReport._usageTime}
                  data={sorted.map((x) => x.usageTime)}
                  tooltip={{
                    visible: true,
                    render: (props) => (
                      <span>
                        {locale.timeUsageReport._usageTime}:{' '}
                        {formatMinutesToTime(props.point.value)}
                      </span>
                    ),
                  }}
                />
              </ChartSeries>
            </Chart>
          </div>
        );
      })}
    </div>
  );
}
